import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';

interface SignUpFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess, onCancel }) => {
  const { signUp, error } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Form validation
    if (!displayName.trim()) {
      setFormError('Name is required');
      return;
    }

    if (!email.trim()) {
      setFormError('Email is required');
      return;
    }

    if (!password) {
      setFormError('Password is required');
      return;
    }

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setFormError(null);
    setLoading(true);

    try {
      await signUp(email, password, displayName);
      setSuccessMessage(
        'Account created successfully! Please check your email to verify your account.'
      );
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by the auth context
      console.error('Sign up error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-center mb-6 text-dark">Create an Account</h2>

      {/* Error message */}
      {(formError || error) && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {formError || error}
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Display name field */}
        <div>
          <label htmlFor="displayName" className="block text-gray-700 font-medium mb-2">
            Name
          </label>
          <input
            type="text"
            id="displayName"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter your name"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            disabled={loading || !!successMessage}
          />
        </div>

        {/* Email field */}
        <div>
          <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
            Email
          </label>
          <input
            type="email"
            id="email"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter your email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={loading || !!successMessage}
          />
        </div>

        {/* Password field */}
        <div>
          <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
            Password
          </label>
          <input
            type="password"
            id="password"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Create a password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={loading || !!successMessage}
          />
          <p className="text-xs text-gray-500 mt-1">Password must be at least 6 characters long</p>
        </div>

        {/* Confirm password field */}
        <div>
          <label htmlFor="confirmPassword" className="block text-gray-700 font-medium mb-2">
            Confirm Password
          </label>
          <input
            type="password"
            id="confirmPassword"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Confirm your password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            disabled={loading || !!successMessage}
          />
        </div>

        {/* Submit button */}
        {!successMessage && (
          <button
            type="submit"
            className={`w-full py-2 px-4 ${
              loading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
            } text-white rounded-lg transition-colors`}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="inline-block animate-spin mr-2">↻</span>
                Creating account...
              </>
            ) : (
              'Sign Up'
            )}
          </button>
        )}

        {/* Sign in link */}
        <div className="text-center mt-4">
          <span className="text-gray-600">Already have an account? </span>
          <Link to="/sign-in" className="text-primary hover:text-primary-dark">
            Sign In
          </Link>
        </div>

        {/* Cancel button (for modal use) */}
        {onCancel && !successMessage && (
          <button
            type="button"
            onClick={onCancel}
            className="w-full py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors mt-2"
            disabled={loading}
          >
            Cancel
          </button>
        )}

        {/* Done button (after successful registration) */}
        {successMessage && (
          <button
            type="button"
            onClick={onCancel || (() => console.log('Done clicked'))}
            className="w-full py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Done
          </button>
        )}
      </form>
    </div>
  );
};

export default SignUpForm;
